import * as Yup from 'yup';

import { CartItem } from 'store/api/cart/types';
import { RefundItem } from 'store/api/refund/types';
import { validateApprovalPassword } from 'utils/validation';

import { validateReturnPayments } from './validate-return-payments';
import { calculateRefundData, calculateMaxRefundAmounts } from './helpers';
import {
  validateItemsSelection,
  validatePaymentsRequired,
} from './validation-helpers';
import {
  FormValues,
  SummaryResult,
  ReturnPayment,
  FormValuesItems,
  GetInitialValues,
  GetValidationSchema,
  GetSummaryCardItems,
} from './types';

export const paymentTitles = [
  '',
  'Amount',
  'Tip',
  'Processing Fee',
  'Free Shipping',
  '',
];
export const paymentTemplate = '2fr repeat(4, 1fr) 64px';
export const paymentCellsMobile = [
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '3/4', col: '1/2' },
  { row: '3/4', col: '2/3' },
];

export const template = '24px 2fr repeat(3, 1fr) 128px';
export const titles = ['Item', 'Quantity', 'Return Price', 'Amount', 'Damaged'];
export const cellsMobile = [
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '1/3' },
  { row: '3/4', col: '1/3' },
  { row: '4/5', col: '1/2' },
  { row: '4/5', col: '2/3' },
];

const sumQuantity = <T extends CartItem | RefundItem>(
  array: T[],
  filterFn: (item: T) => boolean
): number =>
  array.filter(filterFn).reduce((total, curr) => total + curr.quantity, 0);

const getExchangeQuantity = <T extends CartItem>(
  item: T,
  items: T[] = []
): number =>
  sumQuantity(items, (currItem) =>
    Boolean(
      currItem.isExchange &&
        currItem.productId === item.productId &&
        Math.abs(currItem.price) === Math.abs(item.price)
    )
  );

const getRefundedQuantity = ({
  data,
  cart,
}: GetInitialValues): FormValuesItems[] => {
  if (!data?.items || data.items.length === 0) {
    return [];
  }

  const cartItems = cart?.items || [];
  const refundItems =
    data?.refund?.flatMap((refund) => refund.refundItems) || [];
  const exchangeItems =
    data.exchanges?.flatMap((exchange) => exchange.items) || [];

  return data.items.map((item) => {
    const cartExchangeQuantity = getExchangeQuantity(item, cartItems);
    const exchangeQuantity = getExchangeQuantity(item, exchangeItems);
    const refundedQuantity = sumQuantity(
      refundItems,
      (refundItem) => refundItem.checkoutItemId === item.id
    );

    const availableQuantity =
      item.quantity -
      refundedQuantity -
      exchangeQuantity -
      cartExchangeQuantity;

    return {
      ...item,
      selected: false,
      isDamaged: false,
      price: item.price,
      availableQuantity,
      sellPrice: item.price,
      quantity: availableQuantity || 1,
    };
  });
};

export const getInitialValues = ({
  data,
  cart,
}: GetInitialValues): FormValues => ({
  storageId: '',
  returnPayments: [],
  approvalPassword: '',
  items: getRefundedQuantity({ data, cart }) ?? [],
});

export const getValidationSchema = ({
  data,
  isExchange,
  isClosedEvent,
  hideManagerPin,
  isManualCreditPaymentEnabled,
}: GetValidationSchema) => {
  const storageIdSchema = isClosedEvent
    ? Yup.number().positive().required()
    : Yup.number().positive();

  const commonValidationSchema = {
    storageId: storageIdSchema,
    approvalPassword: hideManagerPin ? Yup.string() : validateApprovalPassword,
    items: Yup.array()
      .of(
        Yup.object().shape({
          selected: Yup.boolean(),
          sellPrice: Yup.number(),
          availableQuantity: Yup.number().required(),
          quantity: Yup.number()
            .positive('Quantity must be a positive number')
            .required('Quantity is a required field'),
          price: Yup.number()
            .max(
              Yup.ref('sellPrice'),
              "Can't be more than the amount of the sale"
            )
            .required('Price is a required field'),
        })
      )
      .min(1)
      .required()
      .test(
        'atLeastOneSelected',
        'At least one item must be selected',
        function (items) {
          return validateItemsSelection(items as FormValuesItems[], this);
        }
      ),
  };

  if (isExchange) {
    return Yup.object().shape(commonValidationSchema);
  }

  return Yup.object().shape({
    ...commonValidationSchema,
    returnPayments: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          tip: Yup.number(),
          processingFee: Yup.number(),
          type: Yup.string().required(),
          amount: Yup.number().required(),
          cloverPaymentId: Yup.string().when('type', {
            otherwise: (schema) => schema.notRequired(),
            then: (schema) => schema.required('Credit card must be selected'),
            is: (type: string) =>
              type === 'credit' && !isManualCreditPaymentEnabled,
          }),
        })
      )
      .min(1, 'Return payments must have at least 1 items')
      .test('paymentsValidation', '', function (returnPayments) {
        const isPaymentsRequiredValid = validatePaymentsRequired(
          returnPayments as ReturnPayment[],
          this
        );

        if (isPaymentsRequiredValid !== true) {
          return isPaymentsRequiredValid;
        }

        if (returnPayments && returnPayments.length > 0) {
          return validateReturnPayments(
            returnPayments as ReturnPayment[],
            this,
            data
          );
        }

        return true;
      }),
  });
};

export const getSummaryCardItems = ({
  data,
  values,
  isExchange,
  maxAllowedTipRefund,
  maxAllowedFreeShippingRefund,
  maxAllowedProcessingFeeRefund,
}: GetSummaryCardItems) => {
  const { refundableTotal, selectedItemsAmount } = calculateRefundData({
    data,
    items: values?.items || [],
  });

  const result: SummaryResult[] = [
    { label: 'Sub Total', price: data?.subTotal || 0 },
    {
      price: data?.taxAmount || 0,
      label: `Tax (${data?.tax}%)`,
    },
  ];

  if (data?.tip) {
    result.splice(2, 0, {
      label: 'Tip',
      price: data?.tip,
    });
  }

  if (data?.processingFeesAmount) {
    result.splice(2, 0, {
      label: 'Processing Fee',
      price: data?.processingFeesAmount,
    });
  }

  if (!isExchange) {
    const { maxAllowedRefund } = calculateMaxRefundAmounts(data);
    const totalAvailableRefund = maxAllowedRefund + (data?.taxAmount || 0);

    result.push({
      withSeparate: true,
      price: totalAvailableRefund,
      label: 'Available Refund Amount',
    });

    if (selectedItemsAmount > 0) {
      result.push({
        price: refundableTotal,
        label: 'Selected Items Amount',
      });
    }

    if (maxAllowedFreeShippingRefund) {
      result.push({
        label: 'Available Free Shipping',
        price: maxAllowedFreeShippingRefund,
      });
    }

    if (maxAllowedTipRefund) {
      result.push({
        price: maxAllowedTipRefund,
        label: 'Available Tip Amount',
      });
    }

    if (maxAllowedProcessingFeeRefund) {
      result.push({
        label: 'Available Processing Fee',
        price: maxAllowedProcessingFeeRefund,
      });
    }
  }

  return result;
};
